import {
  Component,
  h,
  Prop,
  Element,
  ComponentInterface,
  Event,
  State,
  EventEmitter,
  Method,
  Listen,
  Host,
} from '@stencil/core';
import { FOCUSABLE_ELEMENTS, keyCodes } from '../../utils/utils';

@Component({
  tag: 'signet-popover',
  styleUrl: 'signet-popover.scss',
})
export class SignetPopover implements ComponentInterface {
  $contents: HTMLElement;
  $control: HTMLElement;
  $focusableEls: HTMLElement[];

  @Element() $el: HTMLElement;

  @Prop() display: 'solid' | 'hollow' | 'clear' | 'link' = 'solid';
  @Prop() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Prop() loading: boolean;
  @Prop() disabled: boolean;
  @Prop() hideText = false;
  @Prop() collapse: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'xs';
  @Prop() buttonText = 'Open Popover';
  @Event() shown: EventEmitter;
  @Event() hidden: EventEmitter;

  @State() isExpanded = false;
  @State() focusIndex = 0;

  @Method()
  async hide() {
    this.isExpanded = false;
    this.focusIndex = 0;
    this.hidden.emit();
    setTimeout(() => this.$control.focus());
  }

  @Method()
  async show() {
    this.isExpanded = true;
    this.shown.emit();

    if (this.$focusableEls.length > 0)
      setTimeout(() => this.$focusableEls[0].focus(), 100);
  }

  @Listen('keydown')
  handleKeyDown(e: KeyboardEvent) {
    switch (e.keyCode) {
      case keyCodes.TAB:
        this.handleTab(e);
        break;
      case keyCodes.ESC:
        this.handleEsc();
        break;
      default:
        break;
    }
  }

  componentDidRender() {
    this.$focusableEls = Array.from(
      this.$contents.querySelectorAll(FOCUSABLE_ELEMENTS)
    );
  }

  componentDidLoad() {
    window.addEventListener('click', (e: MouseEvent) => {
      if (!this.isExpanded) return;

      const $preventCloseElements = Array.from(
        document.querySelectorAll('.prevent-popover-close')
      );
      const isPreventClose =
        this.$el.contains(e.target as HTMLElement) ||
        $preventCloseElements.some((x) => x.contains(e.target as HTMLElement));

      if (isPreventClose) return;

      this.hide();
    });
  }

  private handleEsc() {
    this.hide();
  }

  private handleTab(e: KeyboardEvent) {
    if (!this.isExpanded) return;

    e.preventDefault();

    if (e.shiftKey) {
      this.handleBackwardTab();
    } else {
      this.handleForwardTab();
    }
  }

  private handleBackwardTab() {
    this.focusIndex =
      this.focusIndex === 0 ? this.$focusableEls.length - 1 : --this.focusIndex;
    this.$focusableEls[this.focusIndex].focus();
  }

  private handleForwardTab() {
    this.focusIndex =
      this.$focusableEls.length - 1 === this.focusIndex ? 0 : ++this.focusIndex;
    this.$focusableEls[this.focusIndex].focus();
  }

  private togglePopover() {
    if (this.isExpanded) {
      this.hide();
    } else {
      this.show();
    }
  }

  render() {
    const popoverClasses = {
      'signet-popover': true,
      'show-contents': this.isExpanded,
    };

    const contentClasses = {
      contents: true,
      'popover-contents': true,
    };

    return (
      <Host class={popoverClasses}>
        <signet-button
          button-class="popover-button"
          class="primary"
          haspopup={true}
          expanded={this.isExpanded}
          onClick={() => this.togglePopover()}
          ref={(el) => (this.$control = el.querySelector('button'))}
        >
          <span class={`popover-text ${this.hideText ? 'sr-only' : ''}`}>
            {this.buttonText}
          </span>
        </signet-button>
        <div
          class={contentClasses}
          role={'list'}
          ref={(el) => (this.$contents = el)}
        >
          <div class="popover-item popthing">
            <slot />
          </div>
        </div>
      </Host>
    );
  }
}
